import React, { FC, useEffect, useState } from 'react';
import { BankAccount, CardToCardSettings } from 'interfaces';
import { Spin } from 'antd';
import { BsChevronDown } from 'react-icons/bs';
import { CardNumberSelectionModal } from '../../cardNumberSelectionModal/cardNumberSelectionModal.index';
import { getCardNumbers, getCardToCardSettings } from '../../../../../services';
import { copyToClipboard, detectBank, detectBankByName, formatCardNumber } from 'scripts';
import { TbCopy } from 'react-icons/tb';
import { Button } from '30pay/components';
import { FaPlay } from 'react-icons/fa';
import { CardToCardVideoModal } from './modal/video/videoModal.index';

interface Props {}

export const BuyPaymentCardToCardMethod: FC<Props> = (props) => {
    const [cardNumbersLoading, setCardNumbersLoading] = useState(true);
    const [cardNumbers, setCardNumbers] = useState<Omit<BankAccount, 'iBan'>[]>();
    const [selectedCardNumber, setSelectedCardNumber] = useState<Omit<BankAccount, 'iBan'>>();
    const [cardToCardSettingsLoading, setCardToCardSettingsLoading] = useState<boolean>(false);
    const [cardToCardSettings, setCardToCardSettings] = useState<CardToCardSettings[]>();
    const [selectedCardToCardSetting, setSelectedCardToCardSetting] = useState<CardToCardSettings>();
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [videoModalOpen, setVideoModalOpen] = useState<boolean>(false);

    const onCardNumberSelection = (cardNumber: Omit<BankAccount, 'iBan'>) => {
        setSelectedCardNumber(cardNumber);
        setModalOpen(false);
        setCardToCardSettings(undefined);
        setSelectedCardToCardSetting(undefined);
    };

    const onGetCardToCardSettings = () => {
        getCardToCardSettings(setCardToCardSettingsLoading, setCardToCardSettings);
    };

    useEffect(() => {
        if (cardToCardSettings && cardToCardSettings.length !== 0) setSelectedCardToCardSetting(cardToCardSettings[0]);
    }, [cardToCardSettings]);

    useEffect(() => {
        getCardNumbers(setCardNumbersLoading, setCardNumbers, setSelectedCardNumber);
    }, []);

    return (
        <div className={'flex flex-col'}>
            {cardNumbers && (
                <CardNumberSelectionModal
                    open={modalOpen}
                    setOpen={setModalOpen}
                    cardNumbers={cardNumbers}
                    onSelect={onCardNumberSelection}
                />
            )}
            <CardToCardVideoModal isOpen={videoModalOpen} onClose={() => setVideoModalOpen(false)} />
            <label className={'max-lg:text-sm'}>شماره کارت:</label>
            <Spin spinning={cardNumbersLoading}>
                <div
                    onClick={() => setModalOpen(true)}
                    className={
                        'flex justify-between items-center border border-primary-40 rounded-[28px] text-base px-6 h-14 max-lg:h-12 max-lg:text-sm mt-2 clickable'
                    }
                >
                    {selectedCardNumber && (
                        <>
                            <div className={'flex items-center gap-4'}>
                                <img
                                    src={detectBank(selectedCardNumber?.cardNumber)?.bank_logo}
                                    className={'w-6 h-6'}
                                />

                                <p className="font-bold text-darkPurple-100 flex-1">{selectedCardNumber?.bank?.name}</p>
                            </div>
                            <p className="font-medium text-darkPurple-100">
                                {formatCardNumber(selectedCardNumber?.cardNumber)}
                            </p>

                            <BsChevronDown />
                        </>
                    )}
                </div>
            </Spin>

            {!cardToCardSettings && (
                <Button
                    className={'mt-6'}
                    loading={cardToCardSettingsLoading}
                    type={'primary'}
                    disabled={cardToCardSettingsLoading || !selectedCardNumber}
                    onClick={onGetCardToCardSettings}
                >
                    دریافت اطلاعات واریز
                </Button>
            )}
            {selectedCardToCardSetting && (
                <>
                    <label className={'mt-6 max-lg:text-sm'}>حساب های بانکی صرافی :</label>
                    <Spin spinning={cardToCardSettingsLoading}>
                        <div className={'grid grid-cols-4 max-lg:grid-cols-3 gap-2.5 mt-2'}>
                            {cardToCardSettings?.map((item, index) => {
                                const isSelected = selectedCardToCardSetting === item;
                                return (
                                    <div
                                        key={index}
                                        onClick={() => setSelectedCardToCardSetting(item)}
                                        className={`relative flex flex-col items-center justify-center gap-1.5 rounded-xl border px-1.5 py-2.5 clickable !transition-all ${
                                            isSelected
                                                ? 'border-primary-100 bg-[#F1F8FF80]'
                                                : 'border-primary-40 hover:bg-[#F1F8FF80]'
                                        }`}
                                    >
                                        <span
                                            className={`absolute top-1.5 right-1.5 flex items-center justify-center w-[18px] h-[18px] rounded-full text-[11px] font-bold ${
                                                isSelected
                                                    ? 'bg-primary-100 text-white'
                                                    : 'bg-primary-20 text-primary-100'
                                            }`}
                                        >
                                            {(index + 1).toLocaleString('fa-IR')}
                                        </span>
                                        <img src={detectBankByName(item?.bankName)?.bank_logo} className={'w-7 h-7'} />
                                        <p className="font-bold text-darkPurple-100 text-xs text-center">
                                            {item.bankName}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>
                    </Spin>
                    <div className="flex flex-col gap-4">
                        <div className={'flex flex-col p-4 rounded-lg bg-background mt-6'}>
                            <span className={'font-bold text-darkPurple-100 text-base max-lg:text-sm'}>
                                اطلاعات مقصد
                            </span>
                            <ul className={'flex flex-col gap-2 mt-2'}>
                                {selectedCardToCardSetting?.cardNumber ? (
                                    <li className={'flex items-center justify-between'}>
                                        <span className={'font-bold text-sm max-lg:text-xs'}>شماره کارت مقصد</span>
                                        <span className={'flex items-center gap-2 font-bold text-sm max-lg:text-xs'}>
                                            {formatCardNumber(selectedCardToCardSetting?.cardNumber)}
                                            <TbCopy
                                                className={'clickable'}
                                                onClick={() =>
                                                    copyToClipboard(selectedCardToCardSetting?.cardNumber, 'شماره کارت')
                                                }
                                            />
                                        </span>
                                    </li>
                                ) : null}
                                {selectedCardToCardSetting?.iBan ? (
                                    <li className={'flex items-center justify-between'}>
                                        <span className={'font-bold text-sm max-lg:text-xs'}>شماره شبا مقصد</span>
                                        <span className={'flex items-center gap-2 font-bold text-sm max-lg:text-xs'}>
                                            {selectedCardToCardSetting?.iBan}
                                            <TbCopy
                                                className={'clickable'}
                                                onClick={() =>
                                                    copyToClipboard(selectedCardToCardSetting?.iBan, 'شماره شبا مقصد')
                                                }
                                            />
                                        </span>
                                    </li>
                                ) : null}
                                {selectedCardToCardSetting?.accountNumber ? (
                                    <li className={'flex items-center justify-between'}>
                                        <span className={'font-bold text-sm max-lg:text-xs'}>شماره حساب مقصد</span>
                                        <span className={'flex items-center gap-2 font-bold text-sm max-lg:text-xs'}>
                                            {selectedCardToCardSetting?.accountNumber}
                                            <TbCopy
                                                className={'clickable'}
                                                onClick={() =>
                                                    copyToClipboard(
                                                        selectedCardToCardSetting?.accountNumber,
                                                        'شماره حساب مقصد',
                                                    )
                                                }
                                            />
                                        </span>
                                    </li>
                                ) : null}
                                {selectedCardToCardSetting?.ownerName ? (
                                    <li className={'flex items-center justify-between'}>
                                        <span className={'font-bold text-sm max-lg:text-xs'}>نام صاحب حساب مقصد</span>
                                        <span className={'flex items-center gap-2 font-bold text-sm max-lg:text-xs'}>
                                            {selectedCardToCardSetting?.ownerName}
                                            <TbCopy
                                                className={'clickable'}
                                                onClick={() =>
                                                    copyToClipboard(
                                                        selectedCardToCardSetting?.ownerName,
                                                        'نام صاحب حساب مقصد',
                                                    )
                                                }
                                            />
                                        </span>
                                    </li>
                                ) : null}
                                {selectedCardToCardSetting?.bankName ? (
                                    <li className={'flex items-center justify-between'}>
                                        <span className={'font-bold text-sm max-lg:text-xs'}>نام بانک حساب مقصد</span>
                                        <span className={'flex items-center gap-2 font-bold text-sm max-lg:text-xs'}>
                                            {selectedCardToCardSetting?.bankName}
                                        </span>
                                    </li>
                                ) : null}
                            </ul>
                        </div>
                        <Button
                            className="flex items-center gap-2"
                            type="default"
                            onClick={() => setVideoModalOpen(true)}
                        >
                            <p className="!-mb-1">ویدیو راهنما </p>
                            <FaPlay />
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
};
